import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type ThemeMode = "dark" | "light";

interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const STORAGE_KEY = "hallticket-theme-mode";
const THEME_LINK_ID = "primereact-theme-link";
const OVERRIDE_LINK_ID = "hallticket-prime-overrides-link";

const PRIME_THEME_BY_MODE: Record<ThemeMode, string> = {
  dark: "lara-dark-indigo",
  light: "lara-light-indigo",
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function ensurePrimeThemeLink() {
  let themeLink = document.getElementById(THEME_LINK_ID) as HTMLLinkElement | null;
  if (!themeLink) {
    themeLink = document.createElement("link");
    themeLink.id = THEME_LINK_ID;
    themeLink.rel = "stylesheet";
    document.head.appendChild(themeLink);
  }
  return themeLink;
}

function ensurePrimeOverridesLink() {
  let overrideLink = document.getElementById(OVERRIDE_LINK_ID) as HTMLLinkElement | null;
  if (!overrideLink) {
    overrideLink = document.createElement("link");
    overrideLink.id = OVERRIDE_LINK_ID;
    overrideLink.rel = "stylesheet";
    overrideLink.href = `${process.env.PUBLIC_URL ?? ""}/prime-overrides.css`;
    document.head.appendChild(overrideLink);
  } else {
    document.head.appendChild(overrideLink);
  }
  return overrideLink;
}

function applyPrimeReactTheme(mode: ThemeMode) {
  const theme = PRIME_THEME_BY_MODE[mode];
  const href = `https://unpkg.com/primereact@10.9.7/resources/themes/${theme}/theme.css`;
  const link = ensurePrimeThemeLink();
  const applyOverrides = () => ensurePrimeOverridesLink();
  link.onload = applyOverrides;
  if (link.href !== href) {
    link.href = href;
  } else {
    applyOverrides();
  }
}

function getInitialThemeMode(): ThemeMode {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === "dark" ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialThemeMode);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    localStorage.setItem(STORAGE_KEY, mode);
    applyPrimeReactTheme(mode);
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggleTheme,
    }),
    [mode, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
